import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { fmt } from '../lib/api';
import { Stars } from './Badges';

export default function ProductCard({ p, index = 0 }) {
  const { add } = useCart();
  return (
    <motion.div
      data-testid={`product-card-${index + 1}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className="group relative bg-white rounded-2xl overflow-hidden border border-emerald-950/10 hover:shadow-[0_20px_50px_-20px_rgba(11,43,26,0.35)] hover:-translate-y-1 transition-all duration-300 flex flex-col"
    >
      <Link to={`/product/${p.id}`} className="block relative overflow-hidden bg-forest aspect-square">
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
        />
        {p.badge && (
          <span className="absolute top-3 left-3 bg-flame text-white font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full">
            {p.badge}
          </span>
        )}
        <span className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-widest text-cream/80 bg-forest/70 backdrop-blur px-2.5 py-1 rounded-full">
          {p.sku}
        </span>
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <p className="font-mono text-[10px] uppercase tracking-widest text-moss mb-1.5">{p.category_name}</p>
        <Link to={`/product/${p.id}`} className="font-display font-semibold uppercase tracking-tight text-lg leading-tight text-forest hover:text-flame transition-colors">
          {p.name}
        </Link>
        <div className="flex items-center gap-2 mt-2">
          <Stars rating={p.rating} />
          <span className="text-xs text-sub">({p.review_count})</span>
        </div>
        <div className="mt-auto pt-4 flex items-end justify-between gap-3">
          <div>
            <span className="font-display font-bold text-2xl text-forest">{fmt(p.price)}</span>
            {p.compare_at_price && (
              <span className="ml-2 text-sm text-sub line-through">{fmt(p.compare_at_price)}</span>
            )}
          </div>
          <button
            data-testid={`add-to-cart-button-${index + 1}`}
            onClick={() => add(p)}
            disabled={p.stock <= 0}
            aria-label={`Add ${p.name} to cart`}
            className="grid place-items-center w-11 h-11 rounded-full bg-forest text-cream hover:bg-flame disabled:opacity-30 disabled:hover:bg-forest transition-colors duration-200 shrink-0"
          >
            <ShoppingCart className="w-4.5 h-4.5 w-[18px] h-[18px]" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
